<template>
  <div ref="homeEl" class="home" :class="{ departing }">
    <section class="hero-sheet">
      <div class="hero-shade" :style="{ opacity: (1 - scrollProgress) * 0.72 }"></div>
      <div
        ref="heroEntry"
        class="hero-entry"
        :style="{ opacity: 1 - scrollProgress * 1.15 }"
      >
        <p class="hero-lead km-display">
          <span
            v-for="(word, i) in heroWords"
            :key="i"
            >
            <br v-if="word.break" />
            <span
              class="hero-word"
              :class="{ hi: word.hi, 'hero-en': word.en }"
              >{{ word.t }}</span
            >
          </span>
        </p>
        <p class="hero-agent-note">
          Agent 会与你一起澄清议题、追问立场，并把分散的回答整理成可比较的观点画像。
        </p>
        <div class="agent-capabilities" aria-label="Agent 能力">
          <span>共同定义问题</span><span>循证追问</span><span>聚合群体倾向</span>
        </div>
        <button class="prompt" type="button" :disabled="departing" @click="enterCreation">
          <span>与 Agent 发起一个议题</span>
          <icon-arrow-right />
        </button>
        <p v-if="connectCount > 0" class="hero-hint">
          已联结 {{ connectCount }} 个观点
        </p>
      </div>
      <div class="scroll-hint"><icon-down />下滑看正在发生的议题</div>
    </section>

    <section id="plaza" class="plaza-block">
      <ParticleScroll class="plaza-particle-scroll" :point="0.6" :band="620" :density="2" :size="1.5" :spread="460" :gravity="0.15" :drift="0.7" :swirl="50" :stagger="0.7" :fade="0.6" :settle="1.45" :smoothing="1.3">
        <div class="plaza-particle-content">
      <header class="plaza-head">
        <h1>观点空间</h1>
        <p>正在发生的公开议题，点开即可参与。</p>
      </header>
      <div v-if="topics.length" class="plaza-grid">
        <router-link
          v-for="topic in topics"
          :key="topic.id"
          :to="topicPath(topic)"
          class="plaza-card"
        >
          <i class="node" aria-hidden="true"></i>
          <div>
            <h2>{{ topic.title }}</h2>
            <p>{{ topic.description || "等待不同观点进入。" }}</p>
            <span class="publisher"><span class="publisher-avatar"><img v-if="topic.publisherAvatar" :src="topic.publisherAvatar" alt="" /><b v-else>{{ (topic.publisherName || "K").charAt(0).toUpperCase() }}</b></span>{{ topic.publisherName || "KomeiC 用户" }}</span>
          </div>
          <small class="km-num"
            >{{ topic.confirmedCount }} / {{ topic.participantCount }}</small
          >
          <icon-arrow-right />
        </router-link>
      </div>
      <div v-else class="plaza-empty">
        <p>空间还很安静。</p>
        <router-link class="km-button" to="/space/create"
          >发起第一个议题</router-link
        >
      </div>
        </div>
      </ParticleScroll>
    </section>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import { animate, createTimeline, scrambleText, stagger } from "animejs";
import { IconArrowRight, IconDown } from "@arco-design/web-vue/es/icon";
import { useOpinionScene } from "@/composables/useOpinionScene";
import ParticleScroll from "@/components/canvasui/ParticleScroll.vue";
import { CREATION_SCENE_DURATION, resolveSceneWheelAction } from "@/utils/opinionSceneFlow.mjs";
import { listMyMetricSpaceRecords, listPublicSpaces, PublicMetricSpaceListItem } from "@/api/spaceController";
import { useLoginUserStore } from "@/store/userStore";

const homeEl = ref<HTMLElement>();
const router = useRouter();
const userStore = useLoginUserStore();
const { progress: sceneProgress, selection: sceneSelection } = useOpinionScene();
const departing = ref(false);
const heroEntry = ref<HTMLElement>();
const connectCount = ref(0);
const topics = ref<PublicMetricSpaceListItem[]>([]);
const myCreatedSlugs = ref(new Set());
const topicPath = (topic: PublicMetricSpaceListItem) =>
  myCreatedSlugs.value.has(topic.slug) ? `/s/${topic.slug}/results` : `/s/${topic.slug}`;
const scrollProgress = ref(0);
let keywordPulseTimer: number | undefined;
let snapTimer: number | undefined;
let creationTimer: number | undefined;
let lastScrollTop = 0;
const heroWords = [
  { t: "从一个", break: true },
  { t: "问题", hi: true },
  { t: "开始。" },
  { t: "让不同声音", break: true },
  { t: "汇入", hi: true },
  { t: "。" },
  { t: "No opinion stands alone.", break: true, en: true },
];
const enterCreation = () => {
  if (departing.value) return;
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    router.push("/space/create");
    return;
  }
  departing.value = true;
  sceneSelection.value = true;
  creationTimer = window.setTimeout(async () => {
    const failure = await router.push("/space/create");
    if (failure) {
      departing.value = false;
      sceneSelection.value = false;
    }
  }, CREATION_SCENE_DURATION);
};
const onSceneWheel = (event: WheelEvent) => {
  const action = resolveSceneWheelAction({
    page: "home",
    deltaY: event.deltaY,
    scrollTop: homeEl.value?.scrollTop || 0,
  });
  if (action === "enter-creation") enterCreation();
};
const onHeroMove = (e: PointerEvent) => {
  if (
    !heroEntry.value ||
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  )
    return;
  const cx = e.clientX / window.innerWidth - 0.5;
  const cy = e.clientY / window.innerHeight - 0.5;
  heroEntry.value.style.transform = `translate3d(${cx * -16}px, ${
    cy * -12
  }px, 0)`;
};
const updateProgress = () => {
  const el = homeEl.value;
  if (!el) return;
  const scrollRange = Math.max(1, el.scrollHeight - el.clientHeight);
  scrollProgress.value = Math.min(1, Math.max(0, el.scrollTop / scrollRange));
  sceneProgress.value = scrollProgress.value;
};
const snapScroll = () => {
  const el = homeEl.value;
  if (!el) return;
  const t = el.scrollTop,
    h = el.clientHeight,
    dir = t - lastScrollTop;
  lastScrollTop = t;
  if (snapTimer) window.clearTimeout(snapTimer);
  if (creationTimer) window.clearTimeout(creationTimer);
  snapTimer = window.setTimeout(() => {
    const now = el.scrollTop;
    if (dir > 0 && now >= h * 0.06 && now < h * 1.02) {
      el.scrollTo({ top: h, behavior: "smooth" });
    } else if (dir < 0 && now > h * 0.06 && now < h * 1.1) {
      el.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, 90);
};
const loadStats = async () => {
  try {
    const r = await listPublicSpaces();
    if (r.data.code !== 0) return;
    topics.value = r.data.data || [];
  } catch {
  }
  try {
    if (!userStore.loginUser?.id || userStore.loginUser?.userRole === "notLogin") {
      myCreatedSlugs.value = new Set();
      return;
    }
    const mine = await listMyMetricSpaceRecords();
    const created = (mine.data.data as any)?.created || [];
    myCreatedSlugs.value = new Set(
      created.map((item: any) => String(item.slug || "")).filter(Boolean)
    );
  } catch {
    myCreatedSlugs.value = new Set();
  }
};
onMounted(() => {
  sceneSelection.value = false;
  loadStats();
  updateProgress();
  homeEl.value?.addEventListener("scroll", updateProgress, { passive: true });
  homeEl.value?.addEventListener("scroll", snapScroll, { passive: true });
  window.addEventListener("pointermove", onHeroMove, { passive: true });
  homeEl.value?.addEventListener("wheel", onSceneWheel, { passive: true });
  if (location.hash === "#plaza" || location.hash === "#space") {
    requestAnimationFrame(() =>
      homeEl.value?.scrollTo({ top: homeEl.value.clientHeight, behavior: "auto" })
    );
  }
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

  animate(".hero-word", {
    innerHTML: scrambleText({
      cursor: "░▒▓█",
      from: "center",
      ease: "inOutQuad",
      duration: 1050,
      revealDelay: 90,
      perturbation: 0.3,
      settleDuration: 380,
    }),
    opacity: [0, 1],
    duration: 950,
    delay: stagger(80),
    ease: "outCubic",
  });
  animate(".prompt", {
    opacity: [0, 1],
    translateY: [14, 0],
    duration: 700,
    delay: 980,
    ease: "outCubic",
  });

  keywordPulseTimer = window.setTimeout(() => {
    const keywordPulse = createTimeline({ loop: true, loopDelay: 5000 });
    keywordPulse
      .add(".hero-word.hi", {
        innerHTML: scrambleText({
          cursor: "░▒▓",
          from: "center",
          ease: "outQuad",
          duration: 560,
          perturbation: 0.26,
          settleDuration: 320,
        }),
        scale: { from: 1, to: 1.12 },
        duration: 720,
        ease: "outQuad",
      })
      .add(
        ".hero-word.hi",
        { scale: 1, duration: 620, ease: "inOutQuad" },
        "<+=320"
      );
  }, 1600);
});
onBeforeUnmount(() => {
  if (keywordPulseTimer) window.clearTimeout(keywordPulseTimer);
  if (snapTimer) window.clearTimeout(snapTimer);
  homeEl.value?.removeEventListener("scroll", updateProgress);
  homeEl.value?.removeEventListener("scroll", snapScroll);
  window.removeEventListener("pointermove", onHeroMove);
  homeEl.value?.removeEventListener("wheel", onSceneWheel);
});
</script>

<style scoped>
.home {
  position: relative;
  height: calc(100svh - var(--km-header));
  height: calc(100dvh - var(--km-header));
  overflow-y: auto;
}
.home::-webkit-scrollbar {
  display: none;
}
.hero-sheet {
  position: relative;
  z-index: 2;
  height: calc(100svh - var(--km-header));
  min-height: 560px;
  pointer-events: none;
  scroll-snap-align: start;
}
.hero-shade {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.08), rgba(0, 0, 0, 0.02) 48%, rgba(0, 0, 0, 0.62));
  pointer-events: none;
}
.hero-entry {
  position: absolute;
  z-index: 2;
  left: max(24px, calc((100% - var(--km-page)) / 2));
  bottom: 12vh;
  width: min(640px, calc(100% - 48px));
  pointer-events: auto;
  transition: opacity .4s ease, transform .72s cubic-bezier(.22,.61,.36,1);
}
.home.departing .hero-entry { opacity: 0 !important; transform: translateY(-56px) scale(.94) !important; }
.home.departing .scroll-hint { opacity: 0; }
.hero-lead {
  max-width: 42ch;
  margin: 0 0 20px;
  font-size: clamp(25px, 3.5vw, 38px);
  letter-spacing: -0.01em;
  color: var(--km-muted);
}
.hero-word {
  display: inline-block;
  white-space: pre;
}
.hero-word.hi {
  color: var(--km-ink);
}
.hero-word:not(.hero-en) {
  font-size: .78em;
  font-weight: 560;
  color: var(--km-ink);
}
.hero-word.hero-en {
  font-size: 1em;
  font-weight: 560;
  color: var(--km-muted);
  white-space: nowrap;
}
.hero-word.hero-en.hi {
  color: var(--km-ink);
}
.hero-word:not(.hero-en).hi {
  color: var(--km-ink);
}
.hero-agent-note {
  max-width: 56ch;
  margin: -4px 0 14px;
  color: var(--km-muted);
  font-size: 14px;
  line-height: 1.75;
}
.agent-capabilities {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 18px;
}
.agent-capabilities span {
  padding: 6px 10px;
  border: 1px solid var(--km-line);
  border-radius: 999px;
  background: rgba(9, 13, 18, .48);
  color: rgba(235, 242, 247, .74);
  font-size: 12px;
  letter-spacing: .02em;
}
.prompt {
  min-height: 66px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 22px;
  border: 1px solid var(--km-line-strong);
  border-radius: 8px;
  background: var(--km-overlay);
  color: var(--km-ink);
  text-decoration: none;
  font-size: 17px;
  font-weight: 600;
  backdrop-filter: blur(10px);
  width: 100%;
  font-family: inherit;
  cursor: pointer;
  transition: border-color 0.18s, color 0.18s, box-shadow 0.18s;
}
.prompt span {
  padding-right: 16px;
}
.prompt :deep(svg) {
  flex: 0 0 auto;
  color: var(--km-muted);
  transition: color 0.18s, transform 0.18s;
}
.prompt:hover {
  border-color: var(--km-signal);
  color: var(--km-signal);
  box-shadow: 0 0 0 3px var(--km-signal-soft);
}
.prompt:hover :deep(svg) {
  color: var(--km-signal);
  transform: translateX(3px);
}
.hero-stats {
  margin: 18px 0 0;
  color: var(--km-muted);
  font-size: 13px;
  letter-spacing: 0.01em;
}
.hero-hint {
  margin: 12px 0 0;
  color: var(--km-muted);
  font-size: 12px;
  opacity: 0.8;
}
.scroll-hint {
  position: absolute;
  z-index: 2;
  right: 32px;
  bottom: 30px;
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--km-muted);
  font-size: 13px;
}
.plaza-block {
  position: relative;
  z-index: 1;
  height: calc(100svh - var(--km-header));
  height: calc(100dvh - var(--km-header));
  min-height: 0;
  box-sizing: border-box;
  width: 100%;
  margin: 0 auto;
  padding: 88px 24px 120px;
  background: transparent;
  scroll-snap-align: start;
  overflow: hidden;
}
.plaza-particle-scroll { height: 100%; min-height: 0; }
.plaza-particle-scroll :deep(> div) { height: 100%; }
.plaza-particle-scroll :deep([style*="overflow: auto"]) { scrollbar-width: none; }
.plaza-particle-scroll :deep([style*="overflow: auto"]::-webkit-scrollbar) { display: none; }
.plaza-particle-content { position: relative; z-index: 1; width: min(100%, var(--km-page)); margin: 0 auto; min-height: calc(100% + 480px); padding-bottom: 480px; box-sizing: border-box; }
.plaza-particle-content::after { content: ""; display: block; height: 480px; margin-top: 24px; opacity: .22; background-image: radial-gradient(circle, rgba(152, 190, 212, .7) 0 1px, transparent 1.5px), radial-gradient(circle, rgba(110, 160, 190, .45) 0 1px, transparent 1.6px); background-size: 34px 30px, 57px 49px; }
.plaza-block::before {
  content: "";
  position: absolute;
  z-index: 0;
  inset: 0;
  pointer-events: none;
  opacity: .22;
  background-image:
    radial-gradient(circle at 12% 18%, rgba(176, 216, 235, .7) 0 1px, transparent 1.5px),
    radial-gradient(circle at 74% 30%, rgba(176, 216, 235, .55) 0 1px, transparent 1.6px),
    radial-gradient(circle at 42% 76%, rgba(176, 216, 235, .48) 0 1px, transparent 1.5px),
    radial-gradient(circle at 88% 82%, rgba(176, 216, 235, .62) 0 1px, transparent 1.5px);
  background-size: 260px 220px, 320px 280px, 240px 260px, 360px 300px;
}
.plaza-head h1 {
  margin: 0;
  font-size: 34px;
  font-weight: 650;
  letter-spacing: -0.015em;
}
.plaza-head p {
  margin: 10px 0 36px;
  color: var(--km-muted);
}
.plaza-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 20px;
}
.prompt:disabled { cursor: wait; }
.hero-note {
  margin: -8px 0 18px;
  color: var(--km-muted);
  font-size: 14px;
  line-height: 1.8;
  animation: hero-note-in .9s .25s ease both;
}
@keyframes hero-note-in { from { opacity: 0; filter: blur(5px); transform: translateY(6px); } to { opacity: 1; filter: blur(0); transform: none; } }
.plaza-card {
  display: grid;
  grid-template-columns: 30px minmax(0, 1fr) auto;
  align-items: center;
  gap: 16px;
  padding: 24px;
  border: 1px solid var(--km-line);
  border-radius: 16px;
  background: var(--km-surface-raised);
  box-shadow: inset 0 1px 0 rgba(255,255,255,.06), 0 16px 32px rgba(0,0,0,.18);
  text-decoration: none;
  transition: border-color 0.18s, transform 0.18s;
}
.plaza-card .node {
  width: 15px;
  height: 17px;
  background: var(--km-surface-soft);
  clip-path: polygon(25% 6.7%,75% 6.7%,100% 50%,75% 93.3%,25% 93.3%,0 50%);
  filter: drop-shadow(0 0 7px rgba(255,255,255,.45));
}
.plaza-card h2 {
  margin: 0 0 8px;
  font-size: 20px;
  font-weight: 600;
}
.plaza-card p {
  margin: 0;
  color: var(--km-muted);
  line-height: 1.6;
}
.publisher{display:flex;align-items:center;gap:8px;margin-top:14px;color:rgba(255,255,255,.58);font-size:12px}.publisher-avatar{display:grid;width:24px;height:24px;place-items:center;overflow:hidden;border:1px solid rgba(255,255,255,.22);border-radius:50%;background: var(--km-bg-deep);color: var(--km-ink)}.publisher-avatar img{width:100%;height:100%;object-fit:cover}.publisher-avatar b{font-size:10px;font-weight:650}
.plaza-card small {
  color: var(--km-muted);
}
.plaza-card > :deep(svg) {
  grid-column: 3;
  color: var(--km-muted);
}
.plaza-card:hover h2 {
  color: var(--km-signal);
}
.plaza-card:hover {
  border-color: var(--km-signal);
  transform: translateY(-2px);
}
.plaza-empty {
  display: grid;
  place-content: center;
  justify-items: center;
  gap: 18px;
  min-height: 260px;
  border: 1px dashed var(--km-line);
  color: var(--km-muted);
}
@media (max-width: 720px) {
  .hero-entry {
    left: 14px;
    bottom: 11vh;
    width: calc(100% - 28px);
  }
  .hero-lead {
    max-width: 36ch;
    font-size: clamp(23px, 7vw, 30px);
    margin-bottom: 16px;
  }
  .prompt {
    min-height: 58px;
    padding: 0 16px;
    font-size: 15px;
  }
  .scroll-hint {
    right: 12px;
    bottom: 12px;
    font-size: 11px;
  }
  .plaza-block {
    padding: 56px 14px 76px;
    width: 100%;
  }
  .plaza-card {
    grid-template-columns: 20px minmax(0, 1fr);
  }
  .plaza-grid {
    grid-template-columns: 1fr;
  }
  .plaza-card small {
    display: none;
  }
}
</style>
